import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Plus, Send, FileText, Download, CheckCircle, Clock, Shield, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  detected: { label: 'Detetada', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
  investigating: { label: 'Em Investigação', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Clock },
  contained: { label: 'Contida', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Shield },
  resolved: { label: 'Resolvida', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
  reported: { label: 'Notificada à CNPD', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Send }
};

const DataBreachesPage = () => {
  const { user } = useOutletContext();
  const [breaches, setBreaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, breach: null });
  const [exportingPDF, setExportingPDF] = useState(null);
  const [formData, setFormData] = useState({
    breach_type: '',
    severity: 'medium',
    date_detected: '',
    description: '',
    data_categories: '',
    affected_user_ids: '',
    measures_taken: '',
    recommendations: ''
  });

  useEffect(() => {
    fetchBreaches();
  }, []);

  const fetchBreaches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/data-breaches`, { withCredentials: true });
      setBreaches(response.data);
    } catch (error) {
      console.error('Error fetching breaches:', error);
      toast.error('Erro ao carregar violações');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        breach_type: formData.breach_type,
        severity: formData.severity,
        date_detected: new Date(formData.date_detected).toISOString(),
        description: formData.description,
        data_categories: formData.data_categories.split(',').map(s => s.trim()),
        affected_user_ids: formData.affected_user_ids.split(',').map(s => s.trim()),
        measures_taken: formData.measures_taken.split(',').map(s => s.trim()),
        recommendations: formData.recommendations.split(',').map(s => s.trim())
      };
      
      await axios.post(`${BACKEND_URL}/api/data-breaches`, payload, { withCredentials: true });
      toast.success('Violação registada com sucesso');
      setDialogOpen(false);
      setFormData({
        breach_type: '',
        severity: 'medium',
        date_detected: '',
        description: '',
        data_categories: '',
        affected_user_ids: '',
        measures_taken: '',
        recommendations: ''
      });
      fetchBreaches();
    } catch (error) {
      console.error('Error creating breach:', error);
      toast.error('Erro ao registar violação');
    }
  };

  const handlePrepareNotifications = async (breachId) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/data-breaches/${breachId}/prepare-notifications`,
        {},
        { withCredentials: true }
      );
      toast.success(`Notificações preparadas: ${response.data.user_notification_count} titulares + CNPD`);
      fetchBreaches();
    } catch (error) {
      console.error('Error preparing notifications:', error);
      toast.error('Erro ao preparar notificações');
    }
  };

  const handleSendNotifications = async (breachId) => {
    if (!confirm('Tem a certeza que deseja enviar as notificações? Esta ação não pode ser revertida.')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/data-breaches/${breachId}/send-notifications`,
        {},
        { withCredentials: true }
      );
      toast.success('Notificações enviadas com sucesso');
      fetchBreaches();
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    }
  };

  const handleExportCNPDPdf = async (breachId) => {
    setExportingPDF(breachId);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/data-breaches/${breachId}/cnpd-pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CNPD_Notificacao_${breachId}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF de notificação CNPD exportado');
    } catch (error) {
      console.error('Error exporting CNPD PDF:', error);
      toast.error('Erro ao exportar PDF CNPD');
    } finally {
      setExportingPDF(null);
    }
  };

  const handleMarkAsReported = async (breachId) => {
    if (!confirm('Confirma que a violação foi reportada à CNPD?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/data-breaches/${breachId}/mark-reported`,
        {},
        { withCredentials: true }
      );
      toast.success('Violação marcada como reportada à CNPD');
      fetchBreaches();
    } catch (error) {
      console.error('Error marking as reported:', error);
      toast.error('Erro ao marcar como reportada');
    }
  };

  const handleUpdateStatus = async (breachId, newStatus) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/data-breaches/${breachId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success('Estado atualizado');
      fetchBreaches();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar estado');
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-amber-100 text-amber-700 border-amber-300',
      low: 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    
    const labels = {
      critical: 'Crítica',
      high: 'Alta',
      medium: 'Média',
      low: 'Baixa'
    };
    
    return <Badge className={styles[severity]}>{labels[severity]}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-12">A carregar...</div>;
  }

  return (
    <div className="space-y-8" data-testid="data-breaches-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Violações de Dados (Art. 33º-34º RGPD)
          </h1>
          <p className="text-slate-600">
            Gestão de incidentes de segurança e notificações obrigatórias
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-breach-button" className="bg-[#EF4444] hover:bg-[#EF4444]/90">
              <Plus className="w-4 h-4 mr-2" />
              Registar Violação
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Registar Nova Violação de Dados</DialogTitle>
              <DialogDescription>
                Conforme Artigo 33º do RGPD - Notificação à autoridade de controlo
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="breach_type">Tipo de Violação *</Label>
                <Input
                  id="breach_type"
                  data-testid="breach-type-input"
                  value={formData.breach_type}
                  onChange={(e) => setFormData({...formData, breach_type: e.target.value})}
                  required
                  placeholder="Ex: Acesso não autorizado, Perda de dados, Ransomware"
                />
              </div>
              
              <div>
                <Label htmlFor="severity">Gravidade *</Label>
                <Select value={formData.severity} onValueChange={(value) => setFormData({...formData, severity: value})}>
                  <SelectTrigger data-testid="breach-severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="critical">Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="date_detected">Data de Deteção *</Label>
                <Input
                  id="date_detected"
                  data-testid="breach-date-input"
                  type="datetime-local"
                  value={formData.date_detected}
                  onChange={(e) => setFormData({...formData, date_detected: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Descrição Detalhada *</Label>
                <Textarea
                  id="description"
                  data-testid="breach-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                  rows={4}
                  placeholder="Descreva o incidente, como foi detetado e as circunstâncias"
                />
              </div>
              
              <div>
                <Label htmlFor="data_categories">Categorias de Dados Afetados (separadas por vírgula) *</Label>
                <Input
                  id="data_categories"
                  data-testid="breach-data-categories-input"
                  value={formData.data_categories}
                  onChange={(e) => setFormData({...formData, data_categories: e.target.value})}
                  required
                  placeholder="Ex: email, nome, morada, número de telefone"
                />
              </div>
              
              <div>
                <Label htmlFor="affected_user_ids">IDs dos Titulares Afetados (separados por vírgula) *</Label>
                <Textarea
                  id="affected_user_ids"
                  data-testid="breach-affected-users-input"
                  value={formData.affected_user_ids}
                  onChange={(e) => setFormData({...formData, affected_user_ids: e.target.value})}
                  required
                  rows={2}
                  placeholder="Ex: user_abc123, user_def456"
                />
              </div>
              
              <div>
                <Label htmlFor="measures_taken">Medidas Tomadas (separadas por vírgula) *</Label>
                <Textarea
                  id="measures_taken"
                  data-testid="breach-measures-input"
                  value={formData.measures_taken}
                  onChange={(e) => setFormData({...formData, measures_taken: e.target.value})}
                  required
                  rows={3}
                  placeholder="Ex: Sistema isolado, Passwords alteradas, Investigação iniciada"
                />
              </div>
              
              <div>
                <Label htmlFor="recommendations">Recomendações aos Titulares (separadas por vírgula) *</Label>
                <Textarea
                  id="recommendations"
                  data-testid="breach-recommendations-input"
                  value={formData.recommendations}
                  onChange={(e) => setFormData({...formData, recommendations: e.target.value})}
                  required
                  rows={3}
                  placeholder="Ex: Altere a sua password, Monitorize a sua conta, Ative autenticação de dois fatores"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="submit-breach-button" type="submit" className="bg-[#EF4444] hover:bg-[#EF4444]/90">
                  Registar Violação
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {breaches.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Nenhuma violação registada</h3>
              <p className="text-slate-600">Todas as violações de dados devem ser registadas aqui.</p>
            </CardContent>
          </Card>
        ) : (
          breaches.map((breach) => (
            <Card key={breach.breach_id} className="border-slate-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CardTitle className="text-lg">{breach.breach_type}</CardTitle>
                      {getSeverityBadge(breach.severity)}
                    </div>
                    <p className="text-sm text-slate-600">{breach.description}</p>
                  </div>
                  {breach.notifications_sent && (
                    <Badge className="bg-green-50 text-green-700 border-green-200">Notificações Enviadas</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">Data de Deteção:</span>
                    <p className="font-medium text-[#0F172A]">
                      {new Date(breach.date_detected).toLocaleString('pt-PT')}
                    </p>
                  </div>
                  <div>
                    <span className="text-slate-600">Titulares Afetados:</span>
                    <p className="font-medium text-[#0F172A]">{breach.affected_user_ids?.length || 0}</p>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm text-slate-600">Categorias de Dados:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {breach.data_categories?.map((cat, idx) => (
                      <Badge key={idx} variant="outline" className="bg-slate-50">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                </div>

                {!breach.notifications_sent && (user.role === 'dpo' || user.role === 'admin') && (
                  <div className="pt-4 border-t border-slate-200 space-y-3">
                    {/* Status update */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">Estado:</span>
                      <Select 
                        value={breach.status || 'detected'} 
                        onValueChange={(value) => handleUpdateStatus(breach.breach_id, value)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="detected">Detetada</SelectItem>
                          <SelectItem value="investigating">Em Investigação</SelectItem>
                          <SelectItem value="contained">Contida</SelectItem>
                          <SelectItem value="resolved">Resolvida</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        data-testid={`export-cnpd-pdf-${breach.breach_id}`}
                        onClick={() => handleExportCNPDPdf(breach.breach_id)}
                        variant="outline"
                        disabled={exportingPDF === breach.breach_id}
                      >
                        {exportingPDF === breach.breach_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        PDF Notificação CNPD
                      </Button>
                      <Button
                        data-testid={`mark-reported-${breach.breach_id}`}
                        onClick={() => handleMarkAsReported(breach.breach_id)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marcar como Reportada
                      </Button>
                      <Button
                        data-testid={`prepare-notifications-${breach.breach_id}`}
                        onClick={() => handlePrepareNotifications(breach.breach_id)}
                        className="bg-[#3B82F6] hover:bg-[#3B82F6]/90"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Preparar Notificações
                      </Button>
                      <Button
                        data-testid={`send-notifications-${breach.breach_id}`}
                        onClick={() => handleSendNotifications(breach.breach_id)}
                        className="bg-[#EF4444] hover:bg-[#EF4444]/90"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Notificações
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Show reported status badge */}
                {breach.reported_to_cnpd && (
                  <div className="pt-4 border-t border-slate-200">
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Reportada à CNPD em {breach.cnpd_reported_at ? new Date(breach.cnpd_reported_at).toLocaleDateString('pt-PT') : 'N/A'}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default DataBreachesPage;